import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { addEstablishmentType, fetchEstablishmentType, updateEstablishmentType, deleteEstablishmentType, updateEstablishmentOrder } from './establishmentAsyncThunk';
import { IAuthResponse } from '../auth/IAuthState';
import { EstablishmentType } from '../../../types';
import { toast } from 'react-toastify';

export interface DeleteEstablishmentType extends IAuthResponse {
    id: string;
}

interface EstablishmentTypeState {
    data: EstablishmentType[]; // Array of categories
    status: 'idle' | 'loading' | 'succeeded' | 'failed'; // Fetching status
    loading: boolean; // Create/Update loading
    error: string | null;
}

const initialState: EstablishmentTypeState = {
    data: [],
    status: 'idle',
    loading: false,
    error: null,
};

// Slice
const EstablishmentTypeSlice = createSlice({
    name: 'establishmentType',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch Categories
        builder
            .addCase(fetchEstablishmentType.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchEstablishmentType.fulfilled, (state, action: PayloadAction<EstablishmentType[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchEstablishmentType.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch establishmentType');
                state.error = action.payload || 'Failed to fetch establishmentType';
            });

        // Add Category
        builder
            .addCase(addEstablishmentType.pending, (state) => {
                state.loading = true;
            })
            .addCase(addEstablishmentType.fulfilled, (state, action: PayloadAction<EstablishmentType>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(addEstablishmentType.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishmentType');
                state.error = action.payload || 'Failed to add establishmentType';
            });

        // Update Category
        builder
            .addCase(updateEstablishmentType.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateEstablishmentType.fulfilled, (state, action: PayloadAction<EstablishmentType>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(updateEstablishmentType.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishmentType');
                state.error = action.payload || 'Failed to update establishmentType';
            });

        // Delete Category
        builder
            .addCase(deleteEstablishmentType.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteEstablishmentType.fulfilled, (state, action: PayloadAction<DeleteEstablishmentType>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data = state.data.filter((item) => item.id !== action.payload.id);
                }
                state.loading = false;
            })
            .addCase(deleteEstablishmentType.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishmentType');
                state.error = action.payload || 'Failed to delete establishmentType';
            })
            //Email Template Update Order
            .addCase(updateEstablishmentOrder.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
            })
            .addCase(updateEstablishmentOrder.fulfilled, (state, action: PayloadAction<EstablishmentType[]>) => {
                state.data = action.payload;
                state.status = 'succeeded';
                state.loading = false;
                toast.success("establishmentType order updated successfully");
            })
            .addCase(updateEstablishmentOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                toast.error(action.payload || "Failed to update establishmentType order");
                state.error = action.payload || "Failed to update establishmentType order";
            });
    },
});

export const selectEstablishmentType = (state: RootState) => state.establishmentType;


export default EstablishmentTypeSlice.reducer;
